import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight, ExternalLink, Lock } from 'lucide-react';

const PITCH_PASSWORD = 'MarksMan@123';

/* ── Password Gate ── */
function PasswordGate({ onUnlock }) {
  const [pwd, setPwd] = useState('');
  const [error, setError] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pwd === PITCH_PASSWORD) {
      sessionStorage.setItem('pitch_access', 'granted');
      onUnlock();
    } else {
      setError(true);
      setPwd('');
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1128] flex items-center justify-center relative overflow-hidden" data-testid="pitch-password-gate">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] bg-[#00709B]/10 rounded-full blur-[120px]" />
      <div className="relative z-10 w-full max-w-sm px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#70C24C]/10 mb-6">
            <Lock className="w-6 h-6 text-[#70C24C]" />
          </div>
          <h1 className="text-3xl font-black text-white tracking-tight mb-2">
            Foresight<span className="text-[#70C24C]">Rx</span>
          </h1>
          <p className="text-sm text-[#687399]">Investor Presentation — Confidential</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            ref={inputRef}
            type="password"
            value={pwd}
            onChange={e => setPwd(e.target.value)}
            placeholder="Enter access password"
            className={`w-full px-4 py-3 rounded-xl bg-[#111A3A] border ${error ? 'border-red-500 shake-x' : 'border-white/10 focus:border-[#70C24C]/50'} text-white text-sm placeholder-[#687399] outline-none transition-colors`}
            data-testid="pitch-password-input"
          />
          {error && <p className="text-xs text-red-400 text-center">Incorrect password. Please try again.</p>}
          <button
            type="submit"
            className="w-full py-3 rounded-xl bg-[#70C24C] text-[#0A1128] text-sm font-bold hover:bg-[#5fad3b] transition-colors"
            data-testid="pitch-password-submit"
          >
            Access Presentation
          </button>
        </form>
        <p className="text-[10px] text-[#687399] text-center mt-6">This presentation is confidential and intended for invited investors only.</p>
      </div>
      <style>{`
        .grid-bg { background-image: radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px); background-size: 40px 40px; }
        .shake-x { animation: shakeX 0.4s ease; }
        @keyframes shakeX { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-8px)} 40%,80%{transform:translateX(8px)} }
      `}</style>
    </div>
  );
}

/* ── Intersection Observer Hook ── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ── Counter ── */
function Counter({ end, suffix = '', prefix = '' }) {
  const [val, setVal] = useState(0);
  const [ref, inView] = useInView(0.3);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = end / 120;
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setVal(end); clearInterval(timer); }
      else setVal(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, end]);
  return <span ref={ref}>{prefix}{val.toLocaleString()}{suffix}</span>;
}

/* ═══════════════════════════════════════════════════════════════
   SLIDES
   ═══════════════════════════════════════════════════════════════ */

/* ── SLIDE 1: COVER ── */
function SlideCover() {
  return (
    <div className="slide-content flex flex-col justify-center items-center text-center relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] bg-[#00709B]/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-[#70C24C]/8 rounded-full blur-[100px]" />
      <div className="relative z-10 max-w-4xl px-8">
        <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#70C24C]/30 bg-[#70C24C]/5 text-[#70C24C] text-xs font-bold uppercase tracking-[0.25em] mb-10">
          <div className="w-2 h-2 rounded-full bg-[#70C24C] animate-pulse" />
          Investor Presentation | Confidential
        </div>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white tracking-tighter leading-[1.05] mb-6">
          Foresight<span className="text-[#70C24C]">Rx</span>
        </h1>
        <p className="text-2xl sm:text-3xl text-white/90 font-light tracking-tight mb-2">
          The Intelligence Infrastructure for
          <br />
          <span className="shimmer-text font-semibold">Biopharma Deal-Making</span>
        </p>
        <p className="text-base text-[#A1A8C3] font-light mt-2 max-w-2xl mx-auto">
          We sit between biotech assets, pharma capital, and licensing decisions globally.
          Deep-tech AI + human intelligence + scientific expertise — reducing inefficiencies
          in billions worth of transactions.
        </p>
        <div className="mt-6 inline-flex items-center gap-3 px-5 py-2.5 rounded-xl bg-[#111A3A] border border-white/10">
          <span className="text-sm text-white font-medium">A MarksMan Healthcare Division</span>
          <div className="w-px h-4 bg-white/15" />
          <span className="text-xs text-[#70C24C] font-bold">Profitable | 7 Cr Revenue | 10+ Years</span>
        </div>
        <p className="text-base text-[#687399] font-light mt-8">
          Seed Round: $3-4M | Pre-Money Valuation: $25-30M
        </p>
        <div className="mt-8 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#70C24C]/5 border border-[#70C24C]/20">
          <div className="w-2 h-2 rounded-full bg-[#70C24C] animate-pulse" />
          <span className="text-xs text-[#70C24C] font-medium">Confirmed commitments from pharma & biotech clients</span>
        </div>
        <div className="mt-8 text-xs text-[#687399] font-light">
          Dr. Amit Dang, Founder & CEO | enquiry@foresightrx.com
        </div>
      </div>
    </div>
  );
}

/* ── SLIDE 2: THE PROBLEM ── */
function SlideProblem() {
  return (
    <div className="slide-content flex flex-col justify-center px-8 lg:px-16">
      <p className="slide-label">The Problem</p>
      <h2 className="slide-heading mb-3">
        Pharma deal-making is broken.
      </h2>

      <div className="grid md:grid-cols-4 gap-4 mb-6">
        {[
          { word: 'Fragmented', desc: 'Assets scattered across 10,000+ biotechs in 45+ markets. No single layer connects them to pharma capital.', color: '#70C24C' },
          { word: 'Inefficient', desc: '$615K+ annual BD cost per company. 40 conference meetings yield 3 leads. 92% of effort produces zero deals.', color: '#00709B' },
          { word: 'Relationship-Driven', desc: 'Deals still happen through who-you-know. No infrastructure to surface the best science to the right buyer.', color: '#70C24C' },
          { word: 'Low Conversion', desc: '12-18 month deal cycles. Every month of delay = lost patent life. Most outreach never converts.', color: '#00709B' },
        ].map((c, i) => (
          <div key={i} className="bg-[#111A3A] border border-white/5 rounded-2xl p-5 text-center">
            <p className="text-xl font-black mb-2" style={{ color: c.color }}>{c.word}</p>
            <p className="text-[11px] text-[#A1A8C3] font-light leading-relaxed">{c.desc}</p>
          </div>
        ))}
      </div>

      <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-5 mb-6">
        <p className="text-sm font-bold text-white mb-3">And the pressure is mounting</p>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { icon: '01', title: 'Asset Explosion', desc: '10,000+ biotech companies globally. Pipeline complexity growing 15% YoY. Too many assets for manual scouting.' },
            { icon: '02', title: 'Licensing Complexity', desc: 'Multi-geography rights, modality-specific terms, regulatory risk stratification. Deals are harder to structure.' },
            { icon: '03', title: 'Cross-Border Expansion', desc: '39% of out-licensing now originates from China. India transitioning from generics to innovation. New corridors emerging.' },
            { icon: '04', title: 'AI Enabling Intelligence', desc: 'For the first time, AI can match assets to BD theses at scale. The infrastructure layer doesn\'t exist yet.' },
          ].map((t, i) => (
            <div key={i} className="text-center">
              <div className="w-8 h-8 rounded-lg bg-[#00709B]/15 flex items-center justify-center mx-auto mb-2">
                <span className="text-[10px] font-bold text-[#00709B]">{t.icon}</span>
              </div>
              <p className="text-xs text-white font-medium mb-1">{t.title}</p>
              <p className="text-[10px] text-[#687399] font-light">{t.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gradient-to-r from-[#70C24C]/10 to-[#00709B]/10 border border-[#70C24C]/25 rounded-2xl p-5 text-center">
        <p className="text-lg text-white font-bold">
          We are fixing this.
        </p>
        <p className="text-sm text-[#A1A8C3] font-light mt-1">
          Building the infrastructure that connects biotech innovation to pharma capital — globally, intelligently, and fast.
        </p>
      </div>
    </div>
  );
}

/* ── SLIDE 3: THE SOLUTION ── */
function SlideSolution() {
  return (
    <div className="slide-content flex flex-col justify-center px-8 lg:px-16">
      <p className="slide-label">The Solution</p>
      <h2 className="slide-heading mb-4">
        We run the partnering engine.
        <br />
        <span className="text-[#687399] text-2xl sm:text-3xl">You get the deals.</span>
      </h2>
      <p className="text-base text-[#A1A8C3] font-light mb-8 max-w-3xl">
        Pharma BD teams subscribe to curated cross-border deal flow — never raw databases,
        never platform logins. Biotech founders engage success-only — no subscriptions, no tooling.
        The intelligence layer is our moat, not our product. Pharma can't replicate this; biotechs can't reach
        these BD desks. We sit in the middle and earn on outcomes.
      </p>

      <div className="grid md:grid-cols-3 gap-5 mb-6">
        {[
          {
            title: 'Conference Intelligence Layer',
            items: ['12+ conferences/year — boots on ground', 'Science pre-qualified before pharma sees it', 'Human-verified asset profiles', 'Relationships, not scraping'],
            color: '#70C24C',
          },
          {
            title: 'AI Intelligence Modules',
            items: ['Specialized scouting across delivery technologies, academic assets, and portfolio opportunities', 'AI-extracted BD intelligence with confidence scoring', 'Continuously enriched from conference interactions', 'Structured for pharma decision-making — not raw data'],
            color: '#00709B',
          },
          {
            title: 'Deal Facilitation Infrastructure',
            items: ['Warm introductions — not cold emails', 'CDA, NCD, due diligence orchestration', 'Deal-ready packages shorten diligence', 'End-to-end: discovery → term sheet'],
            color: '#70C24C',
          },
        ].map((c, i) => (
          <div key={i} className="bg-[#111A3A] border border-white/5 rounded-2xl p-5">
            <div className="w-3 h-3 rounded-full mb-3" style={{ backgroundColor: c.color }} />
            <h4 className="text-sm font-bold text-white mb-3">{c.title}</h4>
            <div className="space-y-2">
              {c.items.map((item, j) => (
                <div key={j} className="flex items-center gap-2">
                  <div className="w-1 h-1 rounded-full bg-[#687399]" />
                  <span className="text-xs text-[#A1A8C3] font-light">{item}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-r from-[#70C24C]/8 to-[#00709B]/8 border border-white/5 rounded-2xl p-4 text-center">
        <p className="text-sm text-white font-medium">
          We are building the <span className="text-[#70C24C] font-bold">infrastructure layer</span> that sits between biotech assets, pharma capital, and licensing decisions globally.
        </p>
      </div>
    </div>
  );
}

/* ── SLIDE 4: MARKSMAN HERITAGE ── */
function SlideHeritage() {
  return (
    <div className="slide-content flex flex-col justify-center px-8 lg:px-16">
      <p className="slide-label">The Foundation</p>
      <h2 className="slide-heading mb-4">
        Not a startup. A profitable healthcare company<br />
        <span className="text-[#70C24C]">launching a platform product.</span>
      </h2>
      <p className="text-base text-[#A1A8C3] font-light mb-6 max-w-3xl">
        ForesightRx is not a pre-revenue startup asking for money to survive. It is a platform product
        built by MarksMan Healthcare — a profitable company with 7 Crore revenue, 37% profit margin,
        and a decade of delivering scientific services to the world's top pharma companies.
        Your capital goes 100% to growth. Not to keeping the lights on.
      </p>

      <div className="bg-gradient-to-r from-[#70C24C]/10 to-[#00709B]/10 border border-[#70C24C]/25 rounded-2xl p-4 mb-8 text-center">
        <p className="text-base text-white font-medium">
          MarksMan is already profitable. ForesightRx has confirmed commitments from both pharma and biotech clients.
          <br />
          <span className="text-[#70C24C] font-bold">This is a growth raise — not a survival raise.</span>
        </p>
      </div>

      <div className="grid md:grid-cols-5 gap-4 mb-8">
        {[
          { val: '10+', label: 'Years Operating', sub: 'Founded 2015' },
          { val: '7 Cr', label: 'FY26 Revenue', sub: '~$830K' },
          { val: '2.6 Cr', label: 'FY26 Profit', sub: '37% margin' },
          { val: '33%', label: 'Revenue CAGR', sub: '6-year average' },
          { val: '400+', label: 'Projects Delivered', sub: 'HEOR & evidence' },
        ].map((m, i) => (
          <div key={i} className="bg-[#111A3A] border border-white/5 rounded-2xl p-5 text-center">
            <p className="text-2xl font-black text-[#70C24C]">{m.val}</p>
            <p className="text-xs text-white font-medium mt-1">{m.label}</p>
            <p className="text-[10px] text-[#687399] mt-0.5">{m.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-6">
          <p className="text-sm font-bold text-white mb-4">Revenue & Profit Trajectory (INR)</p>
          <div className="space-y-3">
            {[
              { year: 'FY20', rev: '2.79 Cr', profit: '0.21 Cr', barRev: '40%', barProfit: '8%' },
              { year: 'FY22', rev: '3.43 Cr', profit: '0.96 Cr', barRev: '50%', barProfit: '37%' },
              { year: 'FY24', rev: '6.86 Cr', profit: '2.03 Cr', barRev: '97%', barProfit: '78%' },
              { year: 'FY26', rev: '7.00 Cr', profit: '2.60 Cr', barRev: '100%', barProfit: '100%' },
            ].map((r, i) => (
              <div key={i} className="space-y-1.5">
                <div className="flex items-center gap-4">
                  <span className="text-xs text-[#687399] font-mono w-10 shrink-0">{r.year}</span>
                  <div className="flex-1 h-4 bg-[#0A1128] rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#00709B] to-[#00709B]/70 transition-all duration-1000" style={{ width: r.barRev }} />
                  </div>
                  <span className="text-xs text-white font-medium w-16 text-right shrink-0">{r.rev}</span>
                  <span className="text-[9px] text-[#687399] w-8 shrink-0">Rev</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs w-10 shrink-0" />
                  <div className="flex-1 h-4 bg-[#0A1128] rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#70C24C] to-[#70C24C]/70 transition-all duration-1000" style={{ width: r.barProfit }} />
                  </div>
                  <span className="text-xs text-[#70C24C] font-medium w-16 text-right shrink-0">{r.profit}</span>
                  <span className="text-[9px] text-[#70C24C] w-8 shrink-0">Profit</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-6">
          <p className="text-sm font-bold text-white mb-4">Why This Changes the Investor Thesis</p>
          <div className="space-y-2 text-sm text-[#A1A8C3] font-light">
            {[
              'REVENUE DAY 1: MarksMan scientific services generate immediate revenue from biotech clients',
              'SELF-SUSTAINING: Core business covers operating costs — seed capital is pure growth fuel',
              'PROVEN DEMAND: 600+ pharma projects = deep understanding of what pharma actually buys',
              'WARM PIPELINE: Confirmed commitments from pharma and biotech clients — not a hypothesis, validated commercial demand',
              'CAPITAL EFFICIENT: Built the entire ForesightRx platform with ~$120K while running a profitable business',
              'DUAL REVENUE: MarksMan services (today) + ForesightRx platform fees + success fees (scaling)',
            ].map((c, i) => (
              <div key={i} className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#00709B] mt-1.5 shrink-0" /><span>{c}</span></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── SLIDE 5: MARKET SIZE ── */
function SlideMarket() {
  return (
    <div className="slide-content flex flex-col justify-center px-8 lg:px-16">
      <p className="slide-label">Market Opportunity</p>
      <h2 className="slide-heading mb-10">
        The biggest industry nobody has<br />
        <span className="text-[#70C24C]">truly digitized.</span>
      </h2>

      <div className="grid md:grid-cols-3 gap-8 mb-10">
        <div className="text-center bg-[#111A3A] border border-white/5 rounded-2xl p-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#687399] mb-3">TAM</p>
          <p className="text-4xl font-black text-white mb-2"><Counter end={250} prefix="$" suffix="B+" /></p>
          <p className="text-sm text-[#A1A8C3] font-light">Global biopharma licensing deal value (2025). 516+ deals annually and growing 20% YoY.</p>
        </div>
        <div className="text-center bg-[#111A3A] border border-[#00709B]/20 rounded-2xl p-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#687399] mb-3">SAM</p>
          <p className="text-4xl font-black text-[#00709B] mb-2"><Counter end={12} prefix="$" suffix="B" /></p>
          <p className="text-sm text-[#A1A8C3] font-light">Pharma BD spend on intelligence platforms, conference travel, and deal sourcing infrastructure annually.</p>
        </div>
        <div className="text-center bg-[#111A3A] border border-[#70C24C]/20 rounded-2xl p-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#687399] mb-3">SOM</p>
          <p className="text-4xl font-black text-[#70C24C] mb-2"><Counter end={500} prefix="$" suffix="M" /></p>
          <p className="text-sm text-[#A1A8C3] font-light">Capturable market in Year 5 through SaaS subscriptions + success fees on facilitated deals.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-6">
          <p className="text-sm font-bold text-white mb-3">Key Market Drivers</p>
          <div className="space-y-2 text-sm text-[#A1A8C3] font-light">
            {['50% of large pharma revenue now comes from in-licensed assets', 'China-West licensing deals growing — 39% of global out-licensing originated from China in 2025', '516 licensing deals worth $250B+ in 2025 (JP Morgan) — mean deal value $386M (SRS Acquiom)', 'Phase 3 licensing deals at 3-year high — pharma wants de-risked assets'].map((d, i) => (
              <div key={i} className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#70C24C] mt-1.5 shrink-0" /><span>{d}</span></div>
            ))}
          </div>
        </div>
        <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-6">
          <p className="text-sm font-bold text-white mb-3">Geographic Opportunity</p>
          <div className="space-y-2 text-sm text-[#A1A8C3] font-light">
            {['China: 2nd largest biotech market, $20.6B raised (2023), 40 licensing deals/year avg', 'India: Emerging innovation hub — generics-to-innovation transition accelerating', 'APAC accounts for ~1/3 to 1/2 of global innovative drug pipeline', 'Cross-border deals growing fastest — exactly where ForesightRx operates'].map((d, i) => (
              <div key={i} className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#00709B] mt-1.5 shrink-0" /><span>{d}</span></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── SLIDE 6: COMPETITIVE LANDSCAPE ── */
function SlideCompetition() {
  const competitors = [
    { name: 'Citeline / Norstella', val: '$5B', desc: 'Static database. No matching. No introductions. $50K-$150K/yr subscriptions. Data months old.', gap: 'No conference intelligence, no deal facilitation.' },
    { name: 'Evaluate', val: 'Part of $5B Norstella', desc: 'Commercial analytics & forecasting. Reports-focused. No introductions, no BD execution.', gap: 'Analytics, not action. No boots on ground.' },
    { name: 'BIO One-on-One Partnering', val: 'Conference-only', desc: 'Meeting scheduler at BIO. Dies between conferences. No AI, no curation, no follow-up.', gap: 'Temporary tool. No persistent intelligence.' },
    { name: 'GlobalData', val: '$2.3B market cap', desc: 'Broad healthcare intelligence. Not focused on deal facilitation. Static data feeds.', gap: 'Data aggregator, not a deal engine.' },
  ];

  return (
    <div className="slide-content flex flex-col justify-center px-8 lg:px-16">
      <p className="slide-label">Competitive Landscape</p>
      <h2 className="slide-heading mb-4">
        $5B+ incumbents. Zero living intelligence.
      </h2>
      <p className="text-base text-[#A1A8C3] font-light mb-8 max-w-3xl">
        The market is dominated by static databases that charge $50K-$150K/year for data scraped from press releases.
        None of them attend conferences. None of them meet founders. None of them facilitate deals.
      </p>

      <div className="space-y-3 mb-8">
        <div className="grid grid-cols-12 gap-4 text-[10px] font-bold uppercase tracking-wider text-[#687399] px-4">
          <div className="col-span-2">Competitor</div>
          <div className="col-span-1">Valuation</div>
          <div className="col-span-4">What They Do</div>
          <div className="col-span-5">What They're Missing</div>
        </div>
        {competitors.map((c, i) => (
          <div key={i} className="grid grid-cols-12 gap-4 items-center bg-[#111A3A] border border-white/5 rounded-xl p-4 text-sm">
            <div className="col-span-2 text-white font-medium">{c.name}</div>
            <div className="col-span-1 text-[#687399] text-xs">{c.val}</div>
            <div className="col-span-4 text-[#A1A8C3] font-light text-xs">{c.desc}</div>
            <div className="col-span-5 text-[#70C24C] font-medium text-xs">{c.gap}</div>
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-r from-[#70C24C]/10 to-[#00709B]/10 border border-[#70C24C]/20 rounded-2xl p-6 text-center">
        <p className="text-base text-white font-medium">
          ForesightRx is the <span className="text-[#70C24C] font-bold">only platform</span> that combines conference-sourced intelligence + AI matching + human-facilitated deal execution.
        </p>
      </div>
    </div>
  );
}

/* ── SLIDE: PLATFORM ARCHITECTURE ── */
function SlidePlatform() {
  return (
    <div className="slide-content flex flex-col justify-center px-8 lg:px-16">
      <p className="slide-label">The Technology</p>
      <h2 className="slide-heading mb-4">
        Deep-tech architecture. Not a wrapper.
      </h2>
      <p className="text-sm text-[#A1A8C3] font-light mb-6 max-w-3xl">
        This is not GPT with a UI. Our platform combines proprietary data collection,
        multi-dimensional matching algorithms, and a living intelligence layer that gets
        smarter with every conference, every meeting, every deal.
      </p>

      <div className="grid md:grid-cols-4 gap-4 mb-6">
        {[
          {
            layer: 'Data Layer',
            color: '#00709B',
            items: ['Biotech assets indexed across 36 therapeutic areas', 'BD contacts mapped globally', 'Conference-sourced — not scraped', 'Human-verified profiles', 'Real-time pipeline updates', 'Multi-geography coverage'],
          },
          {
            layer: 'Intelligence Modules',
            color: '#70C24C',
            items: ['Formulation / delivery technologies profiled', 'Academic translational assets mapped', 'Off-patent / tail portfolio opportunities verified', 'Digital therapeutics classified', 'AI-extracted BD intelligence', 'Confidence-scored opportunities'],
          },
          {
            layer: 'Matching Engine',
            color: '#00709B',
            items: ['Real-time matching: assets ↔ BD theses', 'Multi-factor: TA, stage, modality, geography', 'Formulation / delivery: format + differentiation', 'Off-patent / tail: divestment + lifecycle', 'Academic: research strength + translational stage', 'Continuously learning from every interaction'],
          },
          {
            layer: 'Deal Execution',
            color: '#70C24C',
            items: ['Engagement graph with deal pipeline tracking', 'Native Gmail API integration', 'AI-drafted introduction emails', 'Buyer-specific shortlist workflows (Abacus, Vitrus)', 'AI auto-exclusion engine (DNC, portfolio, M&A history) with full audit trail', 'Public shareable client status portals (live deal progress)', 'Conference attendee intake pipeline (Claude-classified)', 'CDA/NCD orchestration', 'End-to-end: discovery → term sheet'],
          },
        ].map((l, i) => (
          <div key={i} className="bg-[#111A3A] border border-white/5 rounded-2xl p-5">
            <div className="w-full h-1 rounded-full mb-4" style={{ backgroundColor: l.color }} />
            <p className="text-sm font-bold text-white mb-3">{l.layer}</p>
            <div className="space-y-2">
              {l.items.map((item, j) => (
                <div key={j} className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: l.color }} />
                  <span className="text-[11px] text-[#A1A8C3] font-light">{item}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-[#0A1128] border border-[#00709B]/15 rounded-xl p-4 text-center">
          <p className="text-xs font-bold text-[#00709B] uppercase tracking-wider mb-1">Matching Logic</p>
          <p className="text-[11px] text-[#A1A8C3] font-light">Therapeutic area × clinical stage × modality × geography × deal readiness × pharma BD thesis = ranked match score</p>
        </div>
        <div className="bg-[#0A1128] border border-[#70C24C]/15 rounded-xl p-4 text-center">
          <p className="text-xs font-bold text-[#70C24C] uppercase tracking-wider mb-1">Data Moat</p>
          <p className="text-[11px] text-[#A1A8C3] font-light">Conference-sourced. Human-verified. Relationship-enriched. Every cycle deepens the intelligence. Cannot be replicated from a desk.</p>
        </div>
        <div className="bg-[#0A1128] border border-white/10 rounded-xl p-4 text-center">
          <p className="text-xs font-bold text-white uppercase tracking-wider mb-1">Science + Transactions</p>
          <p className="text-[11px] text-[#A1A8C3] font-light">We understand both. HEOR expertise to evaluate assets. BD infrastructure to execute deals. Full stack.</p>
        </div>
      </div>
    </div>
  );
}

/* ── SLIDE 8: HOW IT WORKS ── */
function SlideHowItWorks() {
  return (
    <div className="slide-content flex flex-col justify-center px-8 lg:px-16">
      <p className="slide-label">The Living Loop</p>
      <h2 className="slide-heading mb-10">
        A compounding intelligence flywheel.<br />
        <span className="text-[#687399] text-2xl sm:text-3xl">Faster every cycle. Smarter every deal.</span>
      </h2>

      <div className="grid grid-cols-6 gap-4 mb-10">
        {[
          { step: '01', title: 'Attend', desc: 'Travel to 12+ global conferences. Meet biotech founders face-to-face.' },
          { step: '02', title: 'Qualify', desc: 'Assess the science, team, data. Only qualified assets enter the system.' },
          { step: '03', title: 'Index', desc: 'Tag by TA, stage, modality, geography, deal readiness in our living DB.' },
          { step: '04', title: 'Match', desc: 'AI maps assets against pharma BD theses in real time. Minutes, not months.' },
          { step: '05', title: 'Facilitate', desc: 'Warm introductions, CDAs, NCDs — in days, not quarters.' },
          { step: '06', title: 'Learn', desc: 'Every engagement enriches the engine. Faster matching. Better deals.' },
        ].map((s, i) => (
          <div key={i} className="bg-[#111A3A] border border-white/5 rounded-2xl p-5 text-center relative">
            <div className="text-[10px] font-bold text-[#70C24C] tracking-wider mb-2">{s.step}</div>
            <h4 className="text-sm font-bold text-white mb-2">{s.title}</h4>
            <p className="text-[11px] text-[#A1A8C3] font-light leading-relaxed">{s.desc}</p>
            {i < 5 && <div className="absolute -right-3 top-1/2 -translate-y-1/2 text-[#687399] z-10 hidden md:block"><ArrowRight className="w-4 h-4" /></div>}
          </div>
        ))}
      </div>

      <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-6 text-center">
        <p className="text-sm text-[#A1A8C3] font-light">
          <span className="text-white font-medium">Speed is the moat.</span> Every cycle makes matching faster and more precise. Pharma gets qualified assets sooner. Biotechs reach the right BD desk faster. Drugs get to patients earlier. This cannot be replicated by scraping press releases from a desk.
        </p>
      </div>
    </div>
  );
}

/* ── SLIDE 9: BUSINESS MODEL ── */
function SlideBusinessModel() {
  return (
    <div className="slide-content flex flex-col justify-center px-8 lg:px-16">
      <p className="slide-label">Business Model</p>
      <h2 className="slide-heading mb-2">
        Two-sided revenue. One engine.
      </h2>
      <p className="text-base text-[#A1A8C3] font-light mb-6 max-w-3xl">
        Pharma BD <span className="text-[#00709B] font-medium">subscribes to outcomes</span> —
        curated deal flow, never raw data, never platform logins.
        Biotech founders <span className="text-[#70C24C] font-medium">pay only on close</span> —
        no subscription, no tooling, no upfront cost.
        Our intelligence layer is the engine that learns from both sides; neither side ever operates it.
      </p>

      <div className="grid md:grid-cols-3 gap-5 mb-6">
        <div className="bg-[#111A3A] border border-[#00709B]/25 rounded-2xl p-6 relative">
          <div className="absolute top-4 right-4 text-[9px] font-bold px-2 py-0.5 rounded-full bg-[#00709B]/15 text-[#00709B]">PREDICTABLE</div>
          <div className="w-3 h-3 rounded-full bg-[#00709B] mb-3" />
          <h4 className="text-lg font-bold text-white mb-1">Pharma Subscription</h4>
          <p className="text-xs text-[#687399] mb-4">Curated intelligence delivered — no platform access</p>
          <p className="text-2xl font-black text-[#00709B] mb-3">$60K-$250K<span className="text-xs font-normal text-[#687399]">/year</span></p>
          <div className="space-y-2 text-xs text-[#A1A8C3] font-light">
            {['Therapeutic-area scouting feeds (China, India, APAC, EU)', 'Deal benchmarks + competitive partnering signals', 'Pre-qualified asset shortlists with our judgment layer', 'Curated intros — biotechs never see your BD thesis'].map((i, k) => (
              <div key={k} className="flex items-start gap-2"><div className="w-1 h-1 rounded-full bg-[#00709B] mt-1.5 shrink-0" /><span>{i}</span></div>
            ))}
          </div>
        </div>

        <div className="bg-[#111A3A] border border-[#70C24C]/25 rounded-2xl p-6 relative">
          <div className="absolute top-4 right-4 text-[9px] font-bold px-2 py-0.5 rounded-full bg-[#70C24C]/15 text-[#70C24C]">HIGH UPSIDE</div>
          <div className="w-3 h-3 rounded-full bg-[#70C24C] mb-3" />
          <h4 className="text-lg font-bold text-white mb-1">Biotech Success Fees</h4>
          <p className="text-xs text-[#687399] mb-4">Zero subscription — pay only when a deal closes</p>
          <p className="text-2xl font-black text-[#70C24C] mb-3">2-5%<span className="text-xs font-normal text-[#687399]"> of upfront + milestones</span></p>
          <div className="space-y-2 text-xs text-[#A1A8C3] font-light">
            {['Paid only on deal close', 'We facilitate CDA, NCD, introductions, diligence', 'Biotechs share data with us — not directly with pharma', 'Mean deal: $386M. At 2% upfront = $7.7M per deal'].map((i, k) => (
              <div key={k} className="flex items-start gap-2"><div className="w-1 h-1 rounded-full bg-[#70C24C] mt-1.5 shrink-0" /><span>{i}</span></div>
            ))}
          </div>
        </div>

        <div className="bg-[#111A3A] border border-white/10 rounded-2xl p-6 relative">
          <div className="absolute top-4 right-4 text-[9px] font-bold px-2 py-0.5 rounded-full bg-white/10 text-[#A1A8C3]">PREMIUM</div>
          <div className="w-3 h-3 rounded-full bg-white mb-3" />
          <h4 className="text-lg font-bold text-white mb-1">Advisory Services</h4>
          <p className="text-xs text-[#687399] mb-4">Scientific & strategic</p>
          <p className="text-2xl font-black text-white mb-3">$25K-$100K<span className="text-xs font-normal text-[#687399]">/project</span></p>
          <div className="space-y-2 text-xs text-[#A1A8C3] font-light">
            {['Asset positioning & BD readiness', 'Manuscripts & publications', 'Early health economic modeling', 'Competitive landscape & benchmarking'].map((i, k) => (
              <div key={k} className="flex items-start gap-2"><div className="w-1 h-1 rounded-full bg-white/50 mt-1.5 shrink-0" /><span>{i}</span></div>
            ))}
          </div>
        </div>
      </div>

      {/* Why this works */}
      <div className="bg-gradient-to-r from-[#00709B]/10 via-[#70C24C]/10 to-white/5 border border-white/5 rounded-2xl p-5">
        <p className="text-xs font-bold text-white uppercase tracking-wider mb-3 text-center">Why The Intelligence Layer Stays Internal</p>
        <div className="grid md:grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs text-[#00709B] font-semibold mb-1">Pharma subscribes to outcomes</p>
            <p className="text-[11px] text-[#687399]">Curated deal flow + benchmarks delivered as a service. Never raw data, never platform access — they pay for our judgment, not our database.</p>
          </div>
          <div>
            <p className="text-xs text-[#70C24C] font-semibold mb-1">We earn the success fee</p>
            <p className="text-[11px] text-[#687399]">Biotechs share confidential data with us, not pharma. We hold the trust layer end-to-end and orchestrate every deal.</p>
          </div>
          <div>
            <p className="text-xs text-white font-semibold mb-1">The intelligence layer is the moat</p>
            <p className="text-[11px] text-[#687399]">Three years of biotech reply data, conference outcomes, and pharma intent signals. Pharma can't self-serve this; nobody else has it.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── SLIDE 10: THE ECONOMICS ── */
function SlideEconomics() {
  return (
    <div className="slide-content flex flex-col justify-center px-8 lg:px-16">
      <p className="slide-label">The Economics of Conferences</p>
      <h2 className="slide-heading mb-4">
        What pharma & biotech spend today<br />
        <span className="text-[#687399]">vs. what they get.</span>
      </h2>

      <div className="grid md:grid-cols-2 gap-8 mb-8">
        <div className="bg-[#111A3A] border border-red-500/20 rounded-2xl p-6">
          <p className="text-sm font-bold text-red-400 mb-4 uppercase tracking-wider">Without ForesightRx</p>
          <div className="space-y-3">
            {[
              { item: 'BIO International (3 people)', cost: '$45,000' },
              { item: 'BioChina delegation', cost: '$15,000' },
              { item: 'BIO-Europe attendance', cost: '$20,000' },
              { item: 'CPHI booth + team', cost: '$35,000' },
              { item: 'Database subscriptions', cost: '$100,000' },
              { item: 'Internal BD team (2 FTEs)', cost: '$400,000' },
              { item: 'Total annual BD cost', cost: '$615,000+', highlight: true },
            ].map((r, i) => (
              <div key={i} className={`flex justify-between items-center ${r.highlight ? 'border-t border-white/10 pt-3 mt-2' : ''}`}>
                <span className={`text-sm ${r.highlight ? 'text-white font-bold' : 'text-[#A1A8C3] font-light'}`}>{r.item}</span>
                <span className={`text-sm font-mono ${r.highlight ? 'text-red-400 font-bold' : 'text-[#687399]'}`}>{r.cost}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-red-400/80 mt-4 font-light">Result: 3-5 qualified leads per year. Maybe 1 deal in 3 years.</p>
        </div>

        <div className="bg-[#111A3A] border border-[#70C24C]/20 rounded-2xl p-6">
          <p className="text-sm font-bold text-[#70C24C] mb-4 uppercase tracking-wider">With ForesightRx</p>
          <div className="space-y-3">
            {[
              { item: 'Intelligence subscription', cost: '$100,000' },
              { item: 'Conference travel', cost: '$0' },
              { item: 'Database subscriptions', cost: 'Replaced' },
              { item: 'BD team augmentation', cost: 'Included' },
              { item: 'Deal facilitation', cost: 'Per-deal' },
              { item: '', cost: '' },
              { item: 'Total annual cost', cost: '$100K-$200K', highlight: true },
            ].map((r, i) => (
              <div key={i} className={`flex justify-between items-center ${r.highlight ? 'border-t border-white/10 pt-3 mt-2' : ''} ${!r.item ? 'opacity-0' : ''}`}>
                <span className={`text-sm ${r.highlight ? 'text-white font-bold' : 'text-[#A1A8C3] font-light'}`}>{r.item}</span>
                <span className={`text-sm font-mono ${r.highlight ? 'text-[#70C24C] font-bold' : 'text-[#687399]'}`}>{r.cost}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#70C24C]/80 mt-4 font-light">Result: 20-50 qualified matches. Pre-qualified introductions. Faster deal cycles.</p>
        </div>
      </div>

      <div className="bg-[#111A3A] border border-[#70C24C]/20 rounded-2xl p-5 text-center">
        <p className="text-lg text-white font-medium">
          <span className="text-[#70C24C] font-bold">70-80% cost reduction</span> for pharma BD teams.
          <span className="text-[#70C24C] font-bold"> 10x more</span> qualified matches per dollar spent.
        </p>
      </div>
    </div>
  );
}

/* ── SLIDE 11: TRACTION ── */
function SlideTraction() {
  return (
    <div className="slide-content flex flex-col justify-center px-8 lg:px-16">
      <p className="slide-label">Early Traction</p>
      <h2 className="slide-heading mb-10">
        Already in the arena. Already generating revenue.
      </h2>

      <div className="grid grid-cols-4 gap-5 mb-10">
        {[
          { val: '7 Cr', label: 'MarksMan Revenue (FY26)', color: '#70C24C' },
          { val: '3,700+', label: 'Biotech assets indexed', color: 'white' },
          { val: '2,200+', label: 'BD contacts mapped', color: 'white' },
          { val: '7,000+', label: 'Pharma BD interactions logged', color: 'white' },
        ].map((m, i) => (
          <div key={i} className="text-center bg-[#111A3A] border border-white/5 rounded-2xl p-5">
            <p className="text-2xl font-black" style={{ color: m.color }}>{m.val}</p>
            <p className="text-[10px] text-[#687399] mt-1">{m.label}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-5 mb-8">
        <div className="bg-[#111A3A] border border-[#70C24C]/20 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-[#70C24C] animate-pulse" />
            <p className="text-sm font-bold text-[#70C24C]">Confirmed Commitments</p>
          </div>
          <div className="space-y-3 text-sm text-[#A1A8C3] font-light">
            {[
              'Written commitments from pharma clients (email confirmed)',
              'Written commitments from biotech companies (email confirmed)',
              'Active deal conversations and introductions ongoing',
              'Conference meetings being scheduled for BioChina & BIO',
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#70C24C] mt-1.5 shrink-0" /><span>{t}</span></div>
            ))}
          </div>
        </div>
        <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-6">
          <p className="text-sm font-bold text-white mb-4">Intelligence Modules — Live & Operational</p>
          <div className="space-y-3 text-sm text-[#A1A8C3] font-light">
            {[
              'AI-powered portfolio matching — deployed and operational',
              'Specialized scouting: delivery technologies, academic assets, tail portfolios, digital therapeutics',
              'Conference-sourced intelligence from 12+ events annually',
              'Continuously enriched with every conference and deal interaction',
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#00709B] mt-1.5 shrink-0" /><span>{t}</span></div>
            ))}
          </div>
        </div>
        <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-6">
          <p className="text-sm font-bold text-white mb-4">Conference Coverage (2026)</p>
          <div className="space-y-3">
            {[
              { name: 'BioChina 2026', loc: 'Shanghai', date: 'Apr 28-29', status: 'Attended', note: '733 attendees indexed via post-conference intake pipeline' },
              { name: 'BIO International', loc: 'San Diego', date: 'Jun 2-5', status: 'Confirmed' },
              { name: 'BIO-Europe', loc: 'Stockholm', date: 'Oct 2026', status: 'Planned' },
              { name: 'CPHI Worldwide', loc: 'Milan', date: 'Oct 2026', status: 'Planned' },
            ].map((c, i) => (
              <div key={i} className="text-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-white font-medium">{c.name}</span>
                    <span className="text-[#687399] ml-2 text-xs">{c.loc} | {c.date}</span>
                  </div>
                  <span className="text-[10px] font-bold text-[#70C24C] px-2 py-0.5 rounded-full bg-[#70C24C]/10 whitespace-nowrap">{c.status}</span>
                </div>
                {c.note && (
                  <p className="text-[10px] text-[#A1A8C3] mt-0.5 ml-0">{c.note}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-[#70C24C]/10 to-[#00709B]/10 border border-[#70C24C]/20 rounded-2xl p-5 text-center">
        <p className="text-sm text-white font-medium">
          This is <span className="text-[#70C24C] font-bold">not a pre-revenue pitch</span>.
          MarksMan generates 7 Cr annually. ForesightRx has confirmed commitments from pharma and biotech clients.
          The platform is live and operational.
        </p>
      </div>
    </div>
  );
}

/* ── SLIDE 12: FINANCIAL PROJECTIONS ── */
function SlideFinancials() {
  return (
    <div className="slide-content flex flex-col justify-center px-8 lg:px-16">
      <p className="slide-label">Financial Projections</p>
      <h2 className="slide-heading mb-8">
        Seed to Series A trajectory.
      </h2>

      <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-6 mb-8">
        <div className="grid grid-cols-6 gap-4 text-center mb-6">
          <div className="text-xs font-bold uppercase tracking-wider text-[#687399]">Metric</div>
          {['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'].map(y => (
            <div key={y} className="text-xs font-bold uppercase tracking-wider text-[#687399]">{y}</div>
          ))}
        </div>
        {[
          { metric: 'Pharma Clients (SaaS)', vals: ['5', '15', '40', '80', '150'] },
          { metric: 'SaaS Revenue', vals: ['$0.5M', '$2.3M', '$8M', '$20M', '$45M'] },
          { metric: 'Deals Facilitated', vals: ['1-2', '3-5', '8-12', '15-20', '25-30'] },
          { metric: 'Success Fee Rev', vals: ['$2M', '$8M', '$20M', '$40M', '$65M'] },
          { metric: 'Total Revenue', vals: ['$2.5M', '$10.3M', '$28M', '$60M', '$110M'], highlight: true },
          { metric: 'Gross Margin', vals: ['55%', '65%', '72%', '78%', '82%'] },
        ].map((r, i) => (
          <div key={i} className={`grid grid-cols-6 gap-4 text-center py-3 ${r.highlight ? 'border-t border-b border-[#70C24C]/20' : i > 0 ? 'border-t border-white/5' : ''}`}>
            <div className={`text-xs text-left ${r.highlight ? 'text-white font-bold' : 'text-[#A1A8C3]'}`}>{r.metric}</div>
            {r.vals.map((v, j) => (
              <div key={j} className={`text-sm font-mono ${r.highlight ? 'text-[#70C24C] font-bold' : 'text-white'}`}>{v}</div>
            ))}
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-[#111A3A] border border-[#00709B]/20 rounded-2xl p-6">
          <p className="text-sm font-bold text-white mb-4">Seed → Series A Bridge (12-18 months)</p>
          <div className="space-y-3">
            {[
              { milestone: 'Close first 2-3 licensing deals', impact: 'Proves success fee model — a single mid-tier deal at 2% = $2-4M in fees' },
              { milestone: 'Grow to 10-15 pharma clients', impact: 'Validates SaaS subscription demand — $1-2M ARR' },
              { milestone: 'Attend 8+ conferences', impact: 'Deepens data moat — 8,000+ assets indexed' },
            ].map((m, i) => (
              <div key={i} className="bg-[#0A1128]/60 rounded-xl p-4">
                <p className="text-sm text-white font-medium">{m.milestone}</p>
                <p className="text-xs text-[#687399] font-light mt-1">{m.impact}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[#111A3A] border border-[#70C24C]/20 rounded-2xl p-6">
          <p className="text-sm font-bold text-white mb-4">Series A Readiness Triggers</p>
          <div className="space-y-3 text-sm text-[#A1A8C3] font-light">
            {[
              '$2-3M ARR from SaaS subscriptions — predictable, recurring',
              '2-3 closed deals proving success fee unit economics',
              'Mean deal value $386M (SRS Acquiom) × 2% = $7.7M per deal. Even mid-tier deals ($100-200M) = $2-4M',
              'Compounding data moat: 8,000+ qualified assets, 2,000+ contacts',
              'Series A target: $10-15M at $60-100M pre-money',
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#70C24C] mt-1.5 shrink-0" /><span>{t}</span></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── SLIDE 13: USE OF FUNDS ── */
function SlideUseOfFunds() {
  const funds = [
    { area: 'Conference & BD Operations', pct: '35%', amount: '$875K', items: ['8-10 conferences/year', 'Travel, registration, booths', 'On-ground biotech scouting'] },
    { area: 'Technology & Platform', pct: '25%', amount: '$625K', items: ['Curated intelligence delivery automation', 'Pharma subscription dashboard (read-only)', 'Data pipeline & AI matching scale'] },
    { area: 'Team & Talent', pct: '25%', amount: '$625K', items: ['1-2 BD hires (APAC + US)', '1 engineer / data scientist', 'TA domain expertise'] },
    { area: 'Working Capital', pct: '15%', amount: '$375K', items: ['Legal & CDA facilitation', 'Operating buffer (18 months)', 'Marketing & brand building'] },
  ];

  return (
    <div className="slide-content flex flex-col justify-center px-8 lg:px-16">
      <p className="slide-label">Use of Funds</p>
      <h2 className="slide-heading mb-10">
        $3-4M to prove the model and reach Series A.
      </h2>

      <div className="grid md:grid-cols-4 gap-5 mb-8">
        {funds.map((f, i) => (
          <div key={i} className="bg-[#111A3A] border border-white/5 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-2xl font-black text-[#70C24C]">{f.pct}</span>
              <span className="text-xs text-[#687399] font-mono">{f.amount}</span>
            </div>
            <h4 className="text-sm font-bold text-white mb-3">{f.area}</h4>
            <div className="space-y-1.5">
              {f.items.map((item, j) => (
                <div key={j} className="flex items-center gap-2">
                  <div className="w-1 h-1 rounded-full bg-[#687399]" />
                  <span className="text-[11px] text-[#A1A8C3] font-light">{item}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-[#111A3A] border border-[#00709B]/20 rounded-2xl p-6">
        <p className="text-sm font-bold text-white mb-3">18-Month Milestones (Seed Round)</p>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { period: '0-6 months', goal: 'Close first licensing deal. Prove success fee model. 8-10 pharma clients.' },
            { period: '6-12 months', goal: 'Close 2nd-3rd deals. Build SaaS subscription tier. $1M+ ARR trajectory.' },
            { period: '12-15 months', goal: 'Regional BD hires operational. 15+ clients. 6,000+ assets indexed.' },
            { period: '15-18 months', goal: 'Series A fundraise. Target: $10-15M at $60-100M pre-money.' },
          ].map((m, i) => (
            <div key={i} className="bg-[#0A1128]/60 rounded-xl p-4">
              <p className="text-xs font-bold text-[#00709B] mb-1">{m.period}</p>
              <p className="text-xs text-[#A1A8C3] font-light">{m.goal}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── SLIDE 14: TEAM ── */
function SlideTeam() {
  return (
    <div className="slide-content flex flex-col justify-center px-8 lg:px-16">
      <p className="slide-label">The Organization</p>
      <h2 className="slide-heading mb-10">
        A profitable company. Now scaling with technology.
      </h2>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-white mb-1">MarksMan Healthcare</h3>
            <p className="text-sm text-[#70C24C] font-medium mb-5">The Revenue-Generating Foundation</p>
            <div className="space-y-3 text-sm text-[#A1A8C3] font-light">
              {[
                '10+ years delivering HEOR, RWE, and medical affairs to global pharma',
                '600+ projects | 30+ domain experts | 37% profit margin',
                '7 Cr FY26 revenue with 33% CAGR — profitable and growing',
                'Headquarters in Hyderabad with international BD office in Singapore',
                'Client base includes top-20 pharma, mid-size biotech, and global CROs',
              ].map((b, i) => (
                <div key={i} className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#70C24C] mt-1.5 shrink-0" /><span>{b}</span></div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-[#111A3A] border border-[#70C24C]/15 rounded-2xl p-6">
            <p className="text-sm font-bold text-white mb-4">The Investor Advantage</p>
            <div className="space-y-3 text-sm text-[#A1A8C3] font-light">
              {[
                'Revenue Day 1: MarksMan services generate immediate revenue from biotech clients — no burn-only phase',
                'Operating costs covered: MarksMan profitability means seed capital goes entirely to growth',
                'Built-in demand: MarksMan pharma clients are natural ForesightRx customers — cross-sell from Day 1',
                'Full-stack value: Evidence synthesis + deal facilitation under one roof — no competitor offers this',
                'Warm pipeline: Confirmed commitments from pharma and biotech clients proving product-market fit NOW',
              ].map((r, i) => (
                <div key={i} className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#00709B] mt-1.5 shrink-0" /><span>{r}</span></div>
              ))}
            </div>
          </div>

          <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-6">
            <p className="text-sm font-bold text-white mb-3">Key Hires (Post-Funding)</p>
            <div className="space-y-3">
              {[
                { role: 'CTO / VP Engineering', focus: 'Scale AI matching engine and curated intelligence delivery' },
                { role: 'VP BD — Americas', focus: 'Enterprise pharma sales, US market expansion' },
                { role: 'VP BD — Asia Pacific', focus: 'China, Korea, Japan biotech scouting' },
              ].map((h, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-lg bg-[#00709B]/15 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-[10px] font-bold text-[#00709B]">{i + 1}</span>
                  </div>
                  <div>
                    <span className="text-sm text-white font-medium">{h.role}</span>
                    <p className="text-xs text-[#687399] font-light">{h.focus}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── SLIDE 15: THE ASK ── */
function SlideAsk() {
  return (
    <div className="slide-content flex flex-col justify-center items-center text-center relative overflow-hidden px-8">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] bg-[#00709B]/8 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/3 right-1/3 w-[400px] h-[400px] bg-[#70C24C]/8 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-4xl w-full">
        <p className="slide-label text-center mb-6">The Ask</p>
        <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tighter leading-tight mb-4">
          You're not funding a startup.
          <br />
          <span className="shimmer-text">You're accelerating a profitable company.</span>
        </h2>
        <p className="text-base text-[#A1A8C3] font-light mb-10 max-w-2xl mx-auto">
          MarksMan is already profitable. ForesightRx has confirmed commitments from pharma and biotech clients.
          Your capital goes 100% to scaling the platform — not to survival.
        </p>

        <div className="grid md:grid-cols-2 gap-6 mb-8 text-left">
          {/* SEED ROUND */}
          <div className="bg-[#111A3A] border border-[#70C24C]/30 rounded-2xl p-8 relative">
            <div className="absolute top-4 right-4 text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#70C24C]/15 text-[#70C24C]">NOW</div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#70C24C] mb-3">Seed Round</p>
            <p className="text-3xl font-black text-white mb-1">$3-4M</p>
            <p className="text-sm text-[#687399] mb-5">Pre-money: $25-30M</p>
            <div className="space-y-2 text-sm text-[#A1A8C3] font-light">
              {[
                'Close first 2-3 licensing deals — prove success fee model',
                'Grow to 15-20 paying pharma clients ($2-3M ARR)',
                'Attend 8-10 global conferences — deepen data moat',
                'Hire 3-4 key BD & tech team members',
                '18-24 month runway to Series A readiness',
              ].map((r, i) => (
                <div key={i} className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#70C24C] mt-1.5 shrink-0" /><span>{r}</span></div>
              ))}
            </div>
          </div>

          {/* SERIES A */}
          <div className="bg-[#111A3A] border border-[#00709B]/20 rounded-2xl p-8 relative opacity-80">
            <div className="absolute top-4 right-4 text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#00709B]/15 text-[#00709B]">12-18 MONTHS</div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00709B] mb-3">Series A</p>
            <p className="text-3xl font-black text-white mb-1">$10-15M</p>
            <p className="text-sm text-[#687399] mb-5">Target pre-money: $60-100M</p>
            <div className="space-y-2 text-sm text-[#A1A8C3] font-light">
              {[
                'Proven unit economics: success fees + SaaS revenue',
                'Scale to 40+ pharma clients globally',
                'Productize curated intelligence subscription tiers (Scout / Track / Source)',
                'Regional BD teams: US, Europe, APAC',
                'Path to $28M+ revenue by Year 3',
              ].map((r, i) => (
                <div key={i} className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#00709B] mt-1.5 shrink-0" /><span>{r}</span></div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-[#111A3A] border border-[#70C24C]/20 rounded-2xl p-6 mb-8 text-left max-w-3xl mx-auto">
          <p className="text-sm font-bold text-white mb-3">Why this is de-risked for investors</p>
          <div className="space-y-2 text-sm text-[#A1A8C3] font-light">
            {[
              'Profitable parent company: 7 Cr revenue (FY26), 37% profit margin, 33% CAGR — MarksMan covers operating costs',
              'Revenue from Day 1: MarksMan scientific services (HEOR, evidence strategy) already sell to biotech clients',
              'Warm leads in pipeline: Confirmed commitments from pharma and biotech clients — validated commercial demand, not hypothesis',
              'Platform is built and operational: Working AI matching, intelligence modules, engagement graph, Gmail integration, live pipeline — not a PowerPoint',
              'Bootstrapped with ~$120K: Built the entire platform while running a profitable business — extreme capital efficiency',
              'One deal pays back: Mean deal value $386M (SRS Acquiom). At 2% success fee = $7.7M. Even a mid-tier $100M deal = $2M',
            ].map((r, i) => (
              <div key={i} className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#70C24C] mt-1.5 shrink-0" /><span>{r}</span></div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-xl text-white font-medium">
            A profitable company. A live platform. Confirmed clients.
            <br />
            <span className="text-[#70C24C] font-bold">The only thing missing is fuel. Let's scale it.</span>
          </p>
          <p className="text-sm text-[#687399]">
            Dr. Amit Dang | enquiry@foresightrx.com | foresightrx.com
          </p>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PITCH DECK PAGE
   ═══════════════════════════════════════════════════════════════ */

const SLIDES = [
  { component: SlideCover, title: 'Cover' },
  { component: SlideProblem, title: 'The Problem' },
  { component: SlideSolution, title: 'The Solution' },
  { component: SlideHeritage, title: 'MarksMan Heritage' },
  { component: SlideMarket, title: 'Market Size' },
  { component: SlideCompetition, title: 'Competition' },
  { component: SlidePlatform, title: 'Platform Architecture' },
  { component: SlideHowItWorks, title: 'Living Loop' },
  { component: SlideBusinessModel, title: 'Business Model' },
  { component: SlideTraction, title: 'Traction' },
  { component: SlideFinancials, title: 'Financials' },
  { component: SlideUseOfFunds, title: 'Use of Funds' },
  { component: SlideTeam, title: 'The Organization' },
  { component: SlideAsk, title: 'The Ask' },
];

export default function PitchDeckPage() {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem('pitch_access') === 'granted');

  if (!unlocked) {
    return <PasswordGate onUnlock={() => setUnlocked(true)} />;
  }

  return <PitchDeckContent />;
}

function PitchDeckContent() {
  const [current, setCurrent] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        setCurrent(c => Math.min(c + 1, SLIDES.length - 1));
      }
      if (e.key === 'ArrowLeft' || e.key === 'Backspace') {
        e.preventDefault();
        setCurrent(c => Math.max(c - 1, 0));
      }
      if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      }
      if (e.key === 'Escape') {
        if (document.fullscreenElement) document.exitFullscreen();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const toggleFullscreen = () => {
    const el = document.getElementById('pitch-deck-container');
    if (!document.fullscreenElement) {
      el?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const SlideComponent = SLIDES[current].component;
  const slideRef = useRef(null);

  return (
    <div id="pitch-deck-container" className="h-screen bg-[#0A1128] flex flex-col copy-protect" data-testid="pitch-deck">
      <style>{`
        .slide-content { min-height: calc(100vh - 64px); }
        .slide-label { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.25em; color: #70C24C; margin-bottom: 0.75rem; }
        .slide-heading { font-size: 2.25rem; font-weight: 800; color: white; letter-spacing: -0.02em; line-height: 1.15; }
        @media (min-width: 1024px) { .slide-heading { font-size: 2.75rem; } }
        .shimmer-text {
          background: linear-gradient(90deg, #70C24C, #00D4AA, #70C24C);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shimmer 3s linear infinite;
        }
        @keyframes shimmer { to { background-position: 200% center; } }
        .grid-bg {
          background-image: radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 40px 40px;
        }
        .slide-nav-dot { width: 6px; height: 6px; border-radius: 50%; transition: all 0.3s; cursor: pointer; }
        .slide-nav-dot.active { background: #70C24C; width: 24px; border-radius: 3px; }
        .slide-nav-dot:not(.active) { background: rgba(255,255,255,0.15); }
        .slide-nav-dot:not(.active):hover { background: rgba(255,255,255,0.3); }
      `}</style>

      {/* Slide Content */}
      <div id="pitch-slide-area" className="flex-1 relative overflow-y-auto">
        <SlideComponent />
      </div>

      {/* Navigation Bar */}
      <div className="h-16 bg-[#111A3A] border-t border-white/5 flex items-center justify-between px-6 lg:px-12 shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setCurrent(c => Math.max(c - 1, 0))}
            disabled={current === 0}
            className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-white disabled:opacity-20 hover:bg-white/10 transition-colors"
            data-testid="prev-slide-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-[#687399] font-mono min-w-[60px] text-center">
            {current + 1} / {SLIDES.length}
          </span>
          <button
            onClick={() => setCurrent(c => Math.min(c + 1, SLIDES.length - 1))}
            disabled={current === SLIDES.length - 1}
            className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-white disabled:opacity-20 hover:bg-white/10 transition-colors"
            data-testid="next-slide-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Slide dots */}
        <div className="hidden md:flex items-center gap-1.5">
          {SLIDES.map((s, i) => (
            <div
              key={i}
              className={`slide-nav-dot ${i === current ? 'active' : ''}`}
              onClick={() => setCurrent(i)}
              title={s.title}
            />
          ))}
        </div>

        <div className="flex items-center gap-4">
          <span className="text-xs text-[#687399] hidden sm:block">{SLIDES[current].title}</span>
          <button
            onClick={toggleFullscreen}
            className="text-xs text-[#00709B] hover:text-white transition-colors font-medium flex items-center gap-1"
            data-testid="fullscreen-btn"
          >
            <ExternalLink className="w-3 h-3" />
            {isFullscreen ? 'Exit' : 'Present'}
          </button>
        </div>
      </div>
    </div>
  );
}
